import { forwardRef, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ActivityService from "../../services/ActivityService";
import ClientService from "../../services/ClientService";
import CompanyService, {
  DataType,
  Entities
} from "../../services/CompanyService";
import FilterService, { FilterCollection } from "../../services/FilterService";
import { TranslationService } from "../../services/TranslationService";
import { Switch } from "../../utils/Utils";
import ActivityTableProvider, {
  ActivityTableContextValues
} from "../client/activity/ActivityTableProvider";
import { ActivityListResponse } from "../client/activity/entities/AcitivityListResponse";
import { InvoiceList } from "../client/invoice/entities/InvoiceListResponse";
import { TableColumn } from "../client/invoice/InvoiceTableItem";
import ToastContext from "../shared/bootstrap/Toast";
import AdvancedFilters, {
  AdvancedFiltersButton,
  TYPE_REPORT
} from "../shared/components/AdvancedFilters";
import Dropdown from "../shared/components/Dropdown";
import MenuButton, { MenuButtonActions } from "../shared/components/MenuButton";
import Table from "../shared/Table";
import { TaskStatus } from "../task/entities/TaskStatus";
import TableContext from "../task/TableContext";
import imgWhatsAppIco from "../../assets/img/ico-whatsapp.png";
import { ReportScheduledEditItem } from "./ReportScheduledEditItem";
import FloatingPanelService from "../shared/FloatingPanel";
import { ScheduledService } from "../../services/ScheduledService";
import { ScheduledListEntities } from "./entities/ScheduledListResponse";
import TabPosition from "../shared/TabPosition";
import { FixToTop } from "../shared/FixToTop";
import { Helmet } from "react-helmet-async";
import { SafeHtml } from "../../components/SafeHtml";
import "../client/invoice/invoiceStyles.css";

const ReportActivity = () => {
  const urlParams = new URLSearchParams(useLocation().search);
  const filterQS = urlParams.get("filter") ?? "";
  const impid = urlParams.get("impid");
  return (
    <ActivityTableProvider pageSize={25} defaultFilter={filterQS} impid={impid}>
      <ReportActivityList />
    </ActivityTableProvider>
  );
};

const ExportButton = ({ exportRequestData }: any) => {
  const exportInvoices = async (reportId: number | null) => {
    await exportRequestData(reportId ?? undefined);
  };
  const exportItems: MenuButtonActions = [
    {
      text: TranslationService.translate.Default,
      action: () => exportInvoices(null)
    },
    ...CompanyService.getReportExports()
      .filter((x) => x.Report === 0)
      .map((x) => ({
        text: x.Name ?? TranslationService.translate.Default,
        action: () => exportInvoices(x.ReportExportID)
      }))
  ];
  return (
    <div className="actionButtonsHeader">
      {CompanyService.canDo("export") && (
        <MenuButton
          text={TranslationService.translate.Export}
          actions={exportItems}
          extraClassName="select-intiza-outline"
          icon="fa-light fa-arrow-down-to-line pe-1"
          hideChevron
        />
      )}
    </div>
  );
};

const ReportActivityList = () => {
  const search = useLocation().search;
  const filterQS = new URLSearchParams(search).get("filter") ?? "";
  const scheduledId = new URLSearchParams(search).get("scheduled");

  const [scheduledReport, setScheduledReport] = useState<
    ScheduledListEntities.ListItem | undefined
  >();

  useEffect(() => {
    if (!scheduledId) return;
    ScheduledService.getScheduledReportList().then((result) => {
      if (result instanceof Error) return;
      const found = result.list.find(
        (x) => x.ScheduledReportID === Number(scheduledId)
      );
      setScheduledReport(found);
    });
  }, [scheduledId]);

  const tableHeaders = getTableHeaders();
  const { translate } = TranslationService;
  const { applySearch, exportRequestData, currentFilter } = useContext(
    TableContext
  ) as ActivityTableContextValues;

  const typeValues = [
    { text: translate.AllActivities, value: 0 },
    { text: translate.OnlyEmails, value: 1 },
    { text: translate.OnlyComments, value: 2 },
    { text: translate.OnlyTasks, value: 3 },
    { text: translate.OnlySMS, value: 4 }
  ];

  if (CompanyService.canDo("newwhatsapp") && CompanyService.canShowWhatsApp()) {
    typeValues.push({ text: translate.OnlyWhatsApp, value: 6 });
  }

  // Filtro exclusivo de este listado: no se agrega en FilterService para no afectar
  // a los demás consumidores de FilterCollection.ReportActivity (resúmenes, etc.)
  const filterOptions = useMemo(() => {
    const baseOptions = FilterService.GetFiltersForPage(
      FilterCollection.ReportActivity
    );
    return baseOptions.map((option) =>
      option.entity === Entities.Activity
        ? {
            ...option,
            definitions: [
              ...option.definitions,
              {
                AdditionalDefinitionItems: [],
                Entity: Entities.Activity,
                Name: translate.ExcludeAutomaticManagement,
                Field: "-118",
                Type: DataType.YesNo
              }
            ]
          }
        : option
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openProgramModal = () => {
    FloatingPanelService.showPanel({
      children: scheduledReport ? (
        <ReportScheduledEditItem
          data={{ ...scheduledReport, Filter: currentFilter }}
        />
      ) : (
        <ReportScheduledEditItem defaultReportId={0} filter={currentFilter} />
      ),
      title: TranslationService.translate.ScheduledReports
    });
  };
  return (
    <TabPosition module="Reports">
      <Helmet>
        <title>Intiza</title>
      </Helmet>
      <div className="container-fluid padding">
        <div className="mh-100 p-0">
          <FixToTop>
            <div className="d-flex justify-content-between pt-3">
              <h2>{TranslationService.translate.ActivityList}</h2>
            </div>

            <div className="genericHeader">
              <div className="d-flex align-content-center justify-content-between col my-2">
                <div className="searcherFilterHeader align-items-center">
                  <ExportButton exportRequestData={exportRequestData} />
                  <AdvancedFiltersButton
                    savedFiltersId={TYPE_REPORT["Activity"]}
                  />
                  <Dropdown
                    quickFilter
                    items={typeValues}
                    onChange={(x) => applySearch(x.toString())}
                  ></Dropdown>
                </div>
                <div className="d-flex align-content-center gap-3">
                  {CompanyService.getSetting("allowscheduledreports") && (
                    <>
                      <Link
                        to={`/${TranslationService.currentLanguage}/report/scheduled`}
                        className="align-content-center"
                      >
                        {TranslationService.translate.ViewScheduledReports}
                      </Link>
                      {CompanyService.canEditScheduledReport() && (
                        <button
                          onClick={openProgramModal}
                          className="btn btn-primary"
                        >
                          {TranslationService.translate.Program}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <AdvancedFilters
              defaultValue={filterQS}
              page={FilterCollection.ReportActivity}
              filterOptions={filterOptions}
            />
          </FixToTop>
          <Table
            emptyMessage={translate.NoDataToShow}
            stickyHeader={true}
            headers={tableHeaders}
            item={ReportActivityItem}
            fillHeight={true}
          ></Table>
        </div>
      </div>
    </TabPosition>
  );
};

export const ActivityContent = forwardRef<
  HTMLTableCellElement,
  { item: ActivityListResponse.Item }
>(function ActivityContentImpl({ item: x }, ref) {
  return (
    <div ref={ref}>
      {x.typeName === "Email" ? (
        EmailDisplay({ item: x })
      ) : x.typeName === "Comment" ? (
        <SafeHtml html={x.Content} />
      ) : x.typeName === "SMS" ? (
        <span>
          <b>{x.smsphone}:</b>
          {x.Content}
        </span>
      ) : (
        x.Content
      )}
    </div>
  );
});

export const ActivityTypeName = (x: ActivityListResponse.Item) => {
  switch (x.typeName) {
    case "Comment":
      return (
        <td className="th-truncate">
          <i className="fa fa-comment-alt-lines"></i>{" "}
          {TranslationService.translate.Comment}
        </td>
      );
    case "Email":
      return (
        <td className="th-truncate">
          <i className="fa fa-envelope"></i>{" "}
          {TranslationService.translate.Email}
        </td>
      );
    case "SMS":
      return (
        <td className="th-truncate">
          <i
            className={"fa fa-sms" + (x.Status === -999 ? " text-danger" : "")}
          ></i>{" "}
          {TranslationService.translate.SMS}
        </td>
      );
    case "Task":
      return (
        <td className="th-truncate">
          <i
            className={
              "fa fa-fw " +
              (Switch(
                [x.Status === TaskStatus.Validated, "fa-check-double"],
                [x.Status === TaskStatus.Fulfilled, "fa-check"]
              ) || "")
            }
          ></i>{" "}
          {TranslationService.translate.Task}
        </td>
      );
    case "Whatsapp":
      return (
        <td className="th-truncate">
          <div className="icon">
            <img
              className="img-fluid"
              src={imgWhatsAppIco}
              alt="icon comment"
            />
          </div>
        </td>
      );
    default:
      return <td></td>;
  }
};

const ReportActivityItem = ({ data }: { data: ActivityListResponse.Item }) => {
  const { translate, currentLanguage } = TranslationService;
  const tableHeaders = getTableHeaders();
  const specialCases = {
    Name: (x: ActivityListResponse.Item) => (
      <td className="is-link th-truncate">
        <span className="w-md">
          <Link to={`/${currentLanguage}/client/details?id=${data.PersonId}`}>
            {x.personName}
          </Link>
        </span>
      </td>
    ),
    Username: (x: ActivityListResponse.Item) => (
      <td className="th-truncate">
        <span className="w-md">
          {x.UserID ? x.Username : x.Chat ? x.Username : ""}
        </span>
      </td>
    ),
    ActivityTypeID: (x: ActivityListResponse.Item) => (
      <td className="th-truncate">
        {
          CompanyService.getActivityTypes().find(
            (y) => y.ActivityTypeID === x.ActivityTypeID
          )?.ActivityTypeName
        }
      </td>
    ),
    Content: (x: ActivityListResponse.Item) => (
      <td className="th-truncate">
        <ActivityContent item={x} />
      </td>
    ),
    hasAttachments: (x: ActivityListResponse.Item) => (
      <td className="th-truncate">
        {x.hasAttachments ? translate.Yes : translate.No}
      </td>
    ),
    typeName: ActivityTypeName
  };
  return (
    <tr key={data.ID}>
      {tableHeaders.map((header) => (
        <TableColumn
          key={header.fieldId}
          data={data as unknown as InvoiceList.Item}
          header={header}
          specialCases={specialCases}
        />
      ))}
    </tr>
  );
};

function getTableHeaders() {
  const clientFields = ClientService.getClientHeaders();
  clientFields.forEach((x) => (x.sorteable = false));

  // Agrandar anchos de las columnas de clientes y agregar truncamiento
  clientFields.forEach((header) => {
    if (header.fieldId === "Name") {
      header.className =
        (header.className || "").replace(/long-text/g, "").trim() +
        " long-text w-300px th-truncate";
    } else if (!header.className || header.className.trim() === "") {
      header.className = "w-200px th-truncate";
    } else {
      if (!header.className.includes("w-")) {
        header.className = header.className.trim() + " w-200px";
      }
      if (!header.className.includes("th-truncate")) {
        header.className = header.className.trim() + " th-truncate";
      }
    }
  });

  const activtyFieldsStart = ActivityService.getActivityHeadersStart();
  const activtyFieldsEnd = ActivityService.getActivityHeadersEnd();

  // Aumentar anchos de los headers de actividad y agregar truncamiento
  activtyFieldsStart.forEach((header) => {
    if (header.className) {
      header.className = header.className
        .replace(/w-120px/g, "w-200px")
        .replace(/w-200px/g, "w-250px");
    } else {
      header.className = "w-200px";
    }
    if (!header.className.includes("th-truncate")) {
      header.className = header.className.trim() + " th-truncate";
    }
  });

  activtyFieldsEnd.forEach((header) => {
    if (header.className) {
      header.className = header.className
        .replace(/w-120px/g, "w-200px")
        .replace(/w-150px/g, "w-200px")
        .replace(/w-400px/g, "w-500px")
        .replace(/w-80px/g, "w-120px");
    } else {
      header.className = "w-200px";
    }
    if (!header.className.includes("th-truncate")) {
      header.className = header.className.trim() + " th-truncate";
    }
  });

  return [...activtyFieldsStart, ...clientFields, ...activtyFieldsEnd];
}

const EmailDisplay = ({ item }: { item: ActivityListResponse.Item }) => {
  const [mailText, setMailText] = useState<string>();
  const { translate } = TranslationService;
  const { showToast } = useContext(ToastContext);

  const onReadMoreClicked = async (id: number) => {
    const result = await ActivityService.getEmailContent({
      id: id.toString(),
      noheader: "0"
    });
    if (result instanceof Error) {
      showToast(translate.ErrorProcessingRequest, undefined, "danger");
      return;
    }
    setMailText(result.content);
  };

  return (
    <>
      {!mailText && (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start"
            }}
          >
            <div>
              <b>{translate.From}:</b> {item.From}
            </div>
            <div>
              <b>{translate.To}:</b> {item.To}
            </div>
            <div className="mb-2">
              <b>{translate.Subject}:</b> {item.Subject}
            </div>
          </div>
        </>
      )}
      <SafeHtml
        html={mailText ? mailText : item.Content}
        style={{ overflow: "auto" }}
      />

      {!mailText && item.HasMoreContent && (
        <button
          className="btn btn-link text-primary pl-0"
          onClick={() => onReadMoreClicked(item.ID)}
        >
          {translate.ReadMore}
        </button>
      )}
    </>
  );
};

export default ReportActivity;
